import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart, Bar } from 'recharts';
import { fetchBotWinRateTimeSeries, WinRateTimeSeriesOptions } from '@/services/conditional-skip-stats-service';
import { Loader2 } from 'lucide-react';

interface WinRateTimelineChartProps {
  botId: string;
  initialTimeRange?: '24h' | '7d' | '30d' | '90d' | 'all';
}

export const WinRateTimelineChart: React.FC<WinRateTimelineChartProps> = ({ 
  botId,
  initialTimeRange = '7d'
}) => {
  const [timeRange, setTimeRange] = useState<WinRateTimeSeriesOptions['timeRange']>(initialTimeRange);
  const [chartType, setChartType] = useState<'period' | 'cumulative' | 'both'>('both');
  
  // Auto-select granularity based on time range
  const granularity: WinRateTimeSeriesOptions['granularity'] = 
    timeRange === '24h' ? 'hour' :
    timeRange === '7d' ? 'day' :
    timeRange === '30d' ? 'day' :
    timeRange === '90d' ? 'week' : 'week';
  
  const { data: timeSeriesData, isLoading } = useQuery({
    queryKey: ['bot-winrate-timeline', botId, timeRange, granularity],
    queryFn: () => fetchBotWinRateTimeSeries(botId, { timeRange, granularity }),
    refetchInterval: 60000, // Refresh every 1 minute
  });
  
  // Color coding function
  const getWinRateColor = (winRate: number) => {
    if (winRate >= 55) return 'hsl(var(--chart-1))'; // Green
    if (winRate >= 45) return 'hsl(var(--chart-3))'; // Yellow
    return 'hsl(var(--chart-5))'; // Red
  };
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    
    const data = payload[0].payload;
    
    return (
      <div className="bg-background border rounded-lg p-3 shadow-lg">
        <p className="font-medium mb-2">{data.period_start}</p>
        
        <div className="space-y-1 text-sm">
          {chartType !== 'cumulative' && (
            <>
              <p className="text-muted-foreground">Period Stats:</p>
              <p style={{ color: getWinRateColor(data.win_rate) }}>
                Win Rate: <span className="font-bold">{data.win_rate.toFixed(1)}%</span>
              </p>
              <p>Trades: {data.total_trades} ({data.wins}W / {data.losses}L)</p>
            </>
          )}
          
          {chartType !== 'period' && (
            <>
              <p className="text-muted-foreground mt-2">Cumulative:</p>
              <p style={{ color: getWinRateColor(data.cumulative_win_rate) }}>
                Win Rate: <span className="font-bold">{data.cumulative_win_rate.toFixed(1)}%</span>
              </p>
              <p>Total: {data.cumulative_total} ({data.cumulative_wins}W / {data.cumulative_losses}L)</p>
            </>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Win Rate Timeline (Filtered)</CardTitle>
            <CardDescription>
              Biểu đồ win rate theo thời gian - Chỉ tính signals đã forward (response_status = 200)
            </CardDescription>
          </div>
          
          <div className="flex gap-2">
            <Select value={timeRange} onValueChange={(v: any) => setTimeRange(v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 giờ</SelectItem>
                <SelectItem value="7d">7 ngày</SelectItem>
                <SelectItem value="30d">30 ngày</SelectItem>
                <SelectItem value="90d">90 ngày</SelectItem>
                <SelectItem value="all">Tất cả</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !timeSeriesData || timeSeriesData.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Chưa có dữ liệu trong khoảng thời gian này
          </div>
        ) : (
          <Tabs value={chartType} onValueChange={(v: any) => setChartType(v)}>
            <TabsList className="mb-4">
              <TabsTrigger value="period">Period WR</TabsTrigger>
              <TabsTrigger value="cumulative">Cumulative WR</TabsTrigger>
              <TabsTrigger value="both">Both</TabsTrigger>
            </TabsList>
            
            <TabsContent value="period" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="period_start" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    yAxisId="left" 
                    label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft' }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    label={{ value: 'Trades', angle: 90, position: 'insideRight' }}
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Win rate line */}
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="win_rate" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "hsl(var(--primary))" }}
                    name="Win Rate (%)"
                  />
                  
                  {/* Trade volume bars */}
                  <Bar 
                    yAxisId="right"
                    dataKey="total_trades" 
                    fill="hsl(var(--muted))"
                    opacity={0.3}
                    name="Trades"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </TabsContent>
            
            <TabsContent value="cumulative" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="period_start" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    yAxisId="left" 
                    label={{ value: 'Cumulative WR (%)', angle: -90, position: 'insideLeft' }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    label={{ value: 'Cumulative Trades', angle: 90, position: 'insideRight' }}
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Cumulative win rate line */}
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="cumulative_win_rate" 
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "hsl(var(--chart-1))" }}
                    name="Cumulative WR (%)"
                  />
                  
                  {/* Cumulative trades area */}
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulative_total"
                    fill="hsl(var(--muted))"
                    stroke="hsl(var(--muted-foreground))"
                    opacity={0.3}
                    name="Cumulative Trades"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </TabsContent>
            
            <TabsContent value="both" className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="period_start" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft' }}
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Period win rate */}
                  <Line 
                    type="monotone" 
                    dataKey="win_rate" 
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "hsl(var(--primary))" }}
                    name="Period WR (%)"
                    strokeDasharray="5 5"
                  />
                  
                  {/* Cumulative win rate */}
                  <Line 
                    type="monotone" 
                    dataKey="cumulative_win_rate" 
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "hsl(var(--chart-1))" }}
                    name="Cumulative WR (%)"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
